import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getUserTown = () => localStorage.getItem("bcnu_user_town");
export const setUserTown = (town) => localStorage.setItem("bcnu_user_town", town);

export const useSmartSearch = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [bizMatches, setBizMatches] = useState([]);
  const [aiMessage, setAiMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const userTown = getUserTown();

  const fetchSuggestions = useCallback(async (q) => {
    try {
      const res = await axios.get(`${API}/search/suggest`, { params: { q } });
      setSuggestions(res.data.suggestions || []);
      setBizMatches(res.data.businesses || []);
      setIsOpen(true);

      const intent = res.data.intent || {};
      if (intent.detected_category && intent.detected_town) {
        setAiMessage(`Looking for ${intent.detected_category} in ${intent.detected_town}?`);
      } else if (intent.detected_category) {
        setAiMessage(`Showing ${intent.detected_category} results`);
      } else if (intent.detected_town) {
        setAiMessage(`Searching in ${intent.detected_town}`);
      } else {
        setAiMessage("");
      }
    } catch (e) {
      console.error("Search suggest error:", e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is module const, setters are stable

  const onQueryChange = useCallback((q) => {
    setQuery(q);
    if (q.length < 2) {
      setSuggestions([]);
      setBizMatches([]);
      setAiMessage("");
      setIsOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 200);
  }, [fetchSuggestions]);

  const handleFullSearch = useCallback(() => {
    if (!query.trim()) return;
    setLoading(true);
    setIsOpen(false);
    navigate(`/directory?search=${encodeURIComponent(query)}&smart=1`);
    setLoading(false);
  }, [query, navigate]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setIsOpen(false);
    if (suggestion.type === "category") {
      navigate(`/directory?category=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === "town") {
      navigate(`/directory?town=${encodeURIComponent(suggestion.value)}`);
    }
  }, [navigate]);

  const handleBizClick = useCallback((biz) => {
    setIsOpen(false);
    navigate(`/directory?search=${encodeURIComponent(biz.name)}`);
  }, [navigate]);

  const handleKeyDown = useCallback((e) => {
    const totalItems = suggestions.length + bizMatches.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else if (selectedIndex >= suggestions.length) {
        handleBizClick(bizMatches[selectedIndex - suggestions.length]);
      } else {
        handleFullSearch();
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  }, [suggestions, bizMatches, selectedIndex, handleSuggestionClick, handleBizClick, handleFullSearch]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setIsOpen(false);
  }, []);

  return {
    query, setQuery: onQueryChange, clearSearch,
    suggestions, bizMatches, aiMessage,
    loading, isOpen, setIsOpen, selectedIndex,
    userTown, handleFullSearch, handleSuggestionClick,
    handleBizClick, handleKeyDown,
  };
};
